import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { get, post } from "../../services/api";
import Select from "react-select";
import { toast } from "react-toastify";
import DataTable from "react-data-table-component";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import logo from "../../assets/img/logo-full.png"; // Added logo import

// Component to display additional student details in expanded rows
const ExpandedComponent = ({ data }) => {
  // Map gender to readable format
  const genderMap = {
    1: "Male",
    2: "Female",
    // Add more mappings if needed
  };
  const gender = genderMap[data.gender] || "N/A";

  // Map statusId to readable format
  const statusMap = {
    1: "Active",
    2: "Inactive",
    // Add more mappings if needed
  };
  const status = statusMap[data.statusId] || "N/A";

  return (
    <div style={{ padding: "8px" }}>
      <p style={{ fontSize: "12px" }}>
        <strong>Gender:</strong> {gender}
      </p>
      <p style={{ fontSize: "12px" }}>
        <strong>Place of Birth:</strong> {data.placeOfBirth || "N/A"}
      </p>
      <p style={{ fontSize: "12px" }}>
        <strong>Religion:</strong> {data.religion || "N/A"}
      </p>
      <p style={{ fontSize: "12px" }}>
        <strong>Address:</strong> {data.address || "N/A"}
      </p>
      <p style={{ fontSize: "12px" }}>
        <strong>Phone:</strong> {data.phone || "N/A"}
      </p>
      <p style={{ fontSize: "12px" }}>
        <strong>Father's Name:</strong> {data.guardianName || "N/A"}
      </p>
      <p style={{ fontSize: "12px" }}>
        <strong>CNIC:</strong> {data.guardianIdentityNo || "N/A"}
      </p>
      <p style={{ fontSize: "12px" }}>
        <strong>Status:</strong> {status}
      </p>
      <p style={{ fontSize: "12px" }}>
        <strong>Email:</strong> {data.email || "N/A"}
      </p>
      <p style={{ fontSize: "12px" }}>
        <strong>IBAN No:</strong> {data.iBanNo || "N/A"}
      </p>
      <p style={{ fontSize: "12px" }}>
        <strong>Registration No:</strong> {data.registrationNo || "N/A"}
      </p>
      <p style={{ fontSize: "12px" }}>
        <strong>Family Code:</strong> {data.familyCode || "N/A"}
      </p>
      <p style={{ fontSize: "12px" }}>
        <strong>Country:</strong> {data.countryName || "N/A"}
      </p>
      <p style={{ fontSize: "12px" }}>
        <strong>City:</strong> {data.cityName || "N/A"}
      </p>
      <p style={{ fontSize: "12px" }}>
        <strong>State:</strong> {data.stateName || "N/A"}
      </p>
      <p style={{ fontSize: "12px" }}>
        <strong>Is Disabled:</strong> {data.isDisabled ? "Yes" : "No"}
      </p>
      <p style={{ fontSize: "12px" }}>
        <strong>Disabled Description:</strong>{" "}
        {data.disabledDescription || "N/A"}
      </p>
      <p style={{ fontSize: "12px" }}>
        <strong>Is Medical:</strong> {data.isMedical ? "Yes" : "No"}
      </p>
      <p style={{ fontSize: "12px" }}>
        <strong>Medical Description:</strong> {data.medicalDescription || "N/A"}
      </p>
      <p style={{ fontSize: "12px" }}>
        <strong>Is On Medication:</strong> {data.isOnMedication ? "Yes" : "No"}
      </p>
      <p style={{ fontSize: "12px" }}>
        <strong>Medication Description:</strong>{" "}
        {data.medicationDescription || "N/A"}
      </p>
    </div>
  );
};

function StudentInformationReport() {
  const token = localStorage.getItem("token");
  const organizationId = localStorage.getItem("organizationId");
  const savedCenterId =
    localStorage.getItem("centerId") === "null"
      ? null
      : localStorage.getItem("centerId");

  const {
    control,
    setValue,
    trigger,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      nationality: { value: "All", label: "All" },
      yearGroup: { value: "all", label: "All" },
      section: { value: "all", label: "All" },
    },
  });

  const [centerId, setCenterId] = useState(savedCenterId || null);
  const [searchTerm, setSearchTerm] = useState("");
  const [centersData, setCentersData] = useState([]);
  const [yearGroupsData, setYearGroupsData] = useState([]);
  const [sectionsData, setSectionsData] = useState([]);
  const [studentsData, setStudentsData] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const nationalityOptions = [
    { value: "All", label: "All" },
    { value: "Saudi", label: "Saudi" },
    { value: "Non-Saudi", label: "Non-Saudi" },
  ];

  // Fetch centers on mount
  useEffect(() => {
    const fetchCenters = async () => {
      try {
        const response = await get(
          `Dropdown/GetAllCentersByOrganizationId?organizationId=${organizationId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setCentersData(response);
        if (savedCenterId) {
          const selectedCenter = response.find(
            (center) => center.id === parseInt(savedCenterId, 10)
          );
          if (selectedCenter) {
            setValue("centerId", {
              value: selectedCenter.id,
              label: selectedCenter.name,
            });
            setCenterId(selectedCenter.id);
            fetchYearGroupsByCenterId(selectedCenter.id);
          }
        }
      } catch (error) {
        console.error("Error fetching centers:", error);
        toast.error("Failed to fetch centers");
      }
    };
    fetchCenters();
  }, []);

  // Fetch year groups by center ID
  const fetchYearGroupsByCenterId = async (centerId) => {
    try {
      if (centerId) {
        const response = await get(
          `CenterClass/GetClassCenterByCenterId?id=${centerId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setYearGroupsData(response);
      } else {
        setYearGroupsData([]);
        setSectionsData([]);
        setStudentsData([]);
        setFilteredStudents([]);
      }
    } catch (error) {
      console.error("Error fetching year groups:", error);
      toast.error("Failed to fetch year groups");
    }
  };

  // Fetch sections by year group ID and center ID
  const fetchSectionsByYearGroupId = async (yearGroupId, centerId) => {
    try {
      if (yearGroupId && centerId) {
        const response = await get(
          `ClassSection/GetAllSectionByCenterClassIdAndCenterId?centerClassId=${yearGroupId}&centerId=${centerId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setSectionsData(response);
      } else {
        setSectionsData([]);
      }
    } catch (error) {
      console.error("Error fetching sections:", error);
      toast.error("Failed to fetch sections");
    }
  };

  // Fetch year groups when center changes
  useEffect(() => {
    if (centerId) {
      fetchYearGroupsByCenterId(centerId);
    }
  }, [centerId]);

  // Fetch sections when year group changes (only if not "all")
  useEffect(() => {
    const watchedYearGroup = watch("yearGroup")?.value;

    if (watchedYearGroup && watchedYearGroup !== "all" && centerId) {
      fetchSectionsByYearGroupId(watchedYearGroup, centerId);
    } else {
      setSectionsData([]);
    }
  }, [watch("yearGroup"), centerId]);

  // Fetch students using the POST API
  const fetchStudents = async () => {
    const centerId = watch("centerId")?.value;
    const yearGroupValue = watch("yearGroup")?.value;
    const sectionValue = watch("section")?.value;
    const nationality = watch("nationality")?.value;
    const rollNo = searchTerm;

    if (!centerId) {
      setStudentsData([]);
      setFilteredStudents([]);
      return;
    }

    const body = {
      centerId: centerId,
      rollNo: rollNo || "",
      isSaudi: { All: 0, Saudi: 1, "Non-Saudi": 2 }[nationality] || 0,
    };

    if (yearGroupValue !== "all") {
      body.classId = yearGroupValue;
    }

    if (sectionValue !== "all" && yearGroupValue !== "all") {
      body.sectionId = sectionValue;
    }

    try {
      const response = await post(
        "Reports/StudentInfoByCenterClassSectionandStudentIdAsync",
        body,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const center = centersData.find((c) => c.id === centerId);
      const centerName = center ? center.name : "N/A";
      const yearGroup =
        yearGroupValue !== "all"
          ? yearGroupsData.find((yg) => yg.id === yearGroupValue)
          : null;
      const className = yearGroup
        ? yearGroup.name
        : yearGroupValue === "all"
        ? "All"
        : "N/A";

      const enrichedData = (response || []).map((student) => ({
        ...student,
        centerName: student.centerName || centerName,
        className: student.className || className,
      }));

      setStudentsData(enrichedData);
      setFilteredStudents(enrichedData);
    } catch (error) {
      console.error("Error fetching students:", error);
      toast.error("Failed to fetch students");
    }
  };

  useEffect(() => {
    fetchStudents();
  }, [
    watch("centerId"),
    watch("yearGroup"),
    watch("section"),
    watch("nationality"),
    searchTerm,
  ]);

  const columns = [
    {
      name: "Sr No.",
      cell: (row, index) => (currentPage - 1) * rowsPerPage + index + 1,
      width: "60px",
    },
    {
      name: "Full Name",
      cell: (row) => {
        let imageSrc = "";
        if (!row.studentImage || row.studentImage === "string") {
          imageSrc = `${process.env.PUBLIC_URL}/image/team/avatar-placeholder.jpg`;
        } else {
          imageSrc = `${process.env.REACT_APP_LIVE_DOMAIN_URL}ExternalImages/${row.imagePath}`;
        }
        return (
          <div className="d-flex align-items-center gap-2">
            <div className="table-listing-image-preview">
              <img src={imageSrc} alt="Image" />
            </div>
            <div>{row.firstName + " " + row.lastName}</div>
          </div>
        );
      },
      sortable: true,
    },
    {
      name: "Roll No.",
      selector: (row) => row.code,
      sortable: true,
    },
    {
      name: "Year Group",
      selector: (row) => row.className || "N/A",
      sortable: true,
    },
    {
      name: "Date of Birth",
      selector: (row) =>
        row.dateOfBirth ? row.dateOfBirth.split("T")[0] : "N/A",
      sortable: true,
    },
    {
      name: "Nationality",
      selector: (row) => row.nationality || "N/A",
      sortable: true,
    },
  ];

  const exportToExcel = () => {
    const data = filteredStudents.map((student, index) => ({
      "Sr No": (currentPage - 1) * rowsPerPage + index + 1,
      "Roll No": student.code,
      "Full Name": `${student.firstName} ${student.lastName}`,
      "Year Group": student.className || "N/A",
      "Date of Birth": student.dateOfBirth
        ? student.dateOfBirth.split("T")[0]
        : "N/A",
      Nationality: student.nationality || "N/A",
      "Admission Date": student.admissionDate || "N/A",
      Gender: { 1: "Male", 2: "Female" }[student.gender] || "N/A",
      "Place of Birth": student.placeOfBirth || "N/A",
      Religion: student.religion || "N/A",
      Address: student.address || "N/A",
      Phone: student.phone || "N/A",
      "Father's Name": student.guardianName || "N/A",
      CNIC: student.guardianIdentityNo || "N/A",
      Status: { 1: "Active", 2: "Inactive" }[student.statusId] || "N/A",
      Email: student.email || "N/A",
      "IBAN No": student.iBanNo || "N/A",
      "Registration No": student.registrationNo || "N/A",
      "Family Code": student.familyCode || "N/A",
      Country: student.countryName || "N/A",
      City: student.cityName || "N/A",
      State: student.stateName || "N/A",
      "Is Disabled": student.isDisabled ? "Yes" : "No",
      "Disabled Description": student.disabledDescription || "N/A",
      "Is Medical": student.isMedical ? "Yes" : "No",
      "Medical Description": student.medicalDescription || "N/A",
      "Is On Medication": student.isOnMedication ? "Yes" : "No",
      "Medication Description": student.medicationDescription || "N/A",
    }));
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Students");
    XLSX.writeFile(workbook, "StudentInformation.xlsx");
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();

    // Add logo
    const logoWidth = 30;
    const logoHeight = 8;
    const logoX = (pageWidth - logoWidth) / 2;
    const logoY = 10;
    doc.addImage(logo, "PNG", logoX, logoY, logoWidth, logoHeight);

    // Add title
    const title = "Student Information Report";
    const titleFontSize = 12;
    doc.setFontSize(titleFontSize);
    doc.setFont("helvetica", "bold");
    const textWidth = doc.getTextWidth(title);
    const titleX = (pageWidth - textWidth) / 2;
    const titleY = logoY + logoHeight + 10;
    doc.text(title, titleX, titleY);

    const visibleColumns = columns.filter((col) => !col.omit);
    const bodyData = filteredStudents.map((student, index) => [
      (currentPage - 1) * rowsPerPage + index + 1,
      `${student.firstName} ${student.lastName}`,
      student.code,
      student.className || "N/A",
      student.dateOfBirth ? student.dateOfBirth.split("T")[0] : "N/A",
      student.nationality || "N/A",
    ]);

    doc.autoTable({
      head: [visibleColumns.map((col) => col.name)],
      body: bodyData,
      theme: "striped",
      startY: titleY + 10,
      headStyles: {
        fillColor: [144, 84, 255],
        textColor: [255, 255, 255],
      },
    });
    doc.save("StudentInformation.pdf");
  };

  useEffect(() => {
    const tooltipTriggerList = Array.from(
      document.querySelectorAll('[data-bs-toggle="tooltip"]')
    );
    tooltipTriggerList.forEach((tooltipTriggerEl) => {
      new window.bootstrap.Tooltip(tooltipTriggerEl);
    });
  }, []);

  return (
    <>
      <div className="page-header d-flex mb-2">
        <h2 className="page-heading fs-7">Student Information Report</h2>
      </div>
      <div className="card">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-2">
              <label className="form-label">
                Center<span className="text-danger fs-9">*</span>
              </label>
              <Controller
                name="centerId"
                control={control}
                rules={{ required: "This field is required." }}
                render={({ field }) => (
                  <Select
                    {...field}
                    options={centersData.map((center) => ({
                      value: center.id,
                      label: center.name,
                    }))}
                    isDisabled={savedCenterId !== "0"}
                    onChange={(selectedOption, actionMeta) => {
                      if (actionMeta.action === "clear") {
                        setValue("centerId", null);
                        setValue("yearGroup", { value: "all", label: "All" });
                        setValue("section", { value: "all", label: "All" });
                        setCenterId(null);
                        setYearGroupsData([]);
                        setSectionsData([]);
                        setStudentsData([]);
                        setFilteredStudents([]);
                      } else {
                        setValue("centerId", selectedOption);
                        setValue("yearGroup", { value: "all", label: "All" });
                        setValue("section", { value: "all", label: "All" });
                        setCenterId(selectedOption.value);
                        fetchYearGroupsByCenterId(selectedOption.value);
                      }
                      trigger("centerId");
                    }}
                    className="react-select-style"
                    classNamePrefix="react-select-style-pref"
                  />
                )}
              />
              {errors.centerId && (
                <small className="text-danger">{errors.centerId.message}</small>
              )}
            </div>
            <div className="col-md-2">
              <label className="form-label">Year Group</label>
              <Controller
                name="yearGroup"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    options={[
                      { value: "all", label: "All" },
                      ...yearGroupsData.map((yg) => ({
                        value: yg.id,
                        label: yg.name,
                      })),
                    ]}
                    onChange={(selectedOption) => {
                      setValue("yearGroup", selectedOption);
                      if (selectedOption.value === "all") {
                        setValue("section", { value: "all", label: "All" });
                      } else {
                        setValue("section", null);
                      }
                    }}
                    className="react-select-style"
                    classNamePrefix="react-select-style-pref"
                  />
                )}
              />
            </div>
            <div className="col-md-2">
              <label className="form-label">Section</label>
              <Controller
                name="section"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    options={[
                      { value: "all", label: "All" },
                      ...sectionsData.map((section) => ({
                        value: section.id,
                        label: section.name,
                      })),
                    ]}
                    onChange={(selectedOption) => {
                      setValue("section", selectedOption);
                    }}
                    className="react-select-style"
                    classNamePrefix="react-select-style-pref"
                  />
                )}
              />
            </div>
            <div className="col-md-2">
              <label className="form-label">
                Nationality<span className="text-danger fs-9">*</span>
              </label>
              <Controller
                name="nationality"
                control={control}
                rules={{ required: "This field is required." }}
                render={({ field }) => (
                  <Select
                    {...field}
                    options={nationalityOptions}
                    onChange={(selectedOption) => {
                      setValue("nationality", selectedOption);
                      trigger("nationality");
                    }}
                    className="react-select-style"
                    classNamePrefix="react-select-style-pref"
                  />
                )}
              />
              {errors.nationality && (
                <small className="text-danger">
                  {errors.nationality.message}
                </small>
              )}
            </div>
            <div className="col-md-2">
              <label className="form-label">Search by Roll No</label>
              <input
                type="text"
                className="form-control"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="col">
              <label className="form-label" style={{ visibility: "hidden" }}>
                Action
              </label>
              <div className="d-flex gap-2">
                <button
                  type="button"
                  data-bs-toggle="tooltip"
                  data-bs-custom-class="custom-tooltip"
                  title="Export to Excel"
                  className="btn btn-outline-success px-2 py-1"
                  onClick={exportToExcel}
                >
                  <i className="fs-8 fa fa-file-excel"></i>
                </button>
                <button
                  type="button"
                  data-bs-toggle="tooltip"
                  data-bs-custom-class="custom-tooltip"
                  title="Export to PDF"
                  className="btn btn-outline-danger px-2 py-1"
                  onClick={exportToPDF}
                >
                  <i className="fs-8 fa fa-file-pdf"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="card mt-3">
        <div className="card-body">
          <DataTable
            columns={columns.filter((col) => !col.omit)}
            data={filteredStudents}
            pagination
            dense
            highlightOnHover={true}
            pointerOnHover={true}
            onChangePage={(page) => setCurrentPage(page)}
            onChangeRowsPerPage={(newRowsPerPage) =>
              setRowsPerPage(newRowsPerPage)
            }
            expandableRows
            expandableRowsComponent={ExpandedComponent}
          />
        </div>
      </div>
    </>
  );
}

export default StudentInformationReport;
